import {
  createContext,
  type FC,
  type PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

type SegmentNameMap = Record<string, string>;

interface BreadcrumbContextValue {
  /**
   * Map from raw URL segment (e.g. a UUID or numeric ID) to a human-readable name.
   */
  segmentNames: SegmentNameMap;
  /**
   * Register one or more segment-to-name mappings.
   * Typically called by entity pages that already fetch their data.
   */
  setSegmentName: (segment: string, name: string) => void;
}

const BreadcrumbContext = createContext<BreadcrumbContextValue | null>(null);

export const useBreadcrumbContext = () => {
  const ctx = useContext(BreadcrumbContext);
  if (!ctx) {
    throw new Error('useBreadcrumbContext must be used within a BreadcrumbProvider');
  }
  return ctx;
};

export const BreadcrumbProvider: FC<PropsWithChildren> = ({ children }) => {
  const [segmentNames, setSegmentNames] = useState<SegmentNameMap>({});

  const setSegmentName = useMemo(
    () => (segment: string, name: string) => {
      setSegmentNames((prev) => {
        if (prev[segment] === name) return prev;
        return { ...prev, [segment]: name };
      });
    },
    [],
  );

  const value = useMemo(() => ({ segmentNames, setSegmentName }), [segmentNames, setSegmentName]);

  return <BreadcrumbContext.Provider value={value}>{children}</BreadcrumbContext.Provider>;
};

/**
 * Convenience hook for entity pages to register a breadcrumb name.
 * Call this with the raw URL segment (ID) and the resolved display name.
 * The name is automatically cleaned up when the component unmounts.
 */
export const useRegisterBreadcrumb = (segment: string | number | undefined, name?: string) => {
  const { setSegmentName } = useBreadcrumbContext();

  useEffect(() => {
    if (segment != null && name) {
      setSegmentName(String(segment), name);
    }
  }, [segment, name, setSegmentName]);
};
